/**
 * @fileoverview A plant's week in a word, on a sliding scale: Thriving, OK, Slowed,
 * Stressed or At risk.
 *
 * Shared by the week badge on grid
 * tiles and list rows (PlantMarks), so a state reads the same wherever it is
 * drawn. The Plants page's weather bar counts the garden's plants in each
 * state as quiet text in the same colours (GardenConditionsBar). The colour deepens down the scale: Thriving
 * is pale green with a check, OK plain, Slowed a neutral grey, Stressed a
 * tint in the colour it leans (cool, warm or wet), and At risk solid in the
 * danger's own colour, ink blue for cold and rust for heat, with a warning
 * mark. The state and its colour are the plants brief's (the backend's
 * plantWeekSaid statusOf; D40).
 *
 * Where: PlantMarks (tiles and rows), the bed's
 * plant rows (LocationPlantList).
 * API: none.
 * Words (en/es): plants.card.state.<thriving|ok|slowed|stressed|atRisk>.
 *
 * @module WeekState
 */

import React from 'react';
import { AlertTriangle, Check } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useTranslation } from '@/shared/hooks/useTranslation';
import { TOKEN_SOLID, TOKEN_TINT } from '@/shared/theme/verdictTokens';
import type { WeekStatus } from '@/shared/api/plantsBrief';

/** What the label says, and how big it is drawn. */
interface WeekStateLabelProps {
  /** The week's state, and for a danger its colour. */
  status: WeekStatus;
  /** Size, padding and corners from the caller: the fold's label is larger than a tile's. */
  className?: string;
  /** Classes for the check or warning mark, to match the caller's text size. */
  iconClassName?: string;
}

/** Each state's colour, but for Stressed and At risk, which take the colour of the way they lean. */
const TONE: Record<Exclude<WeekStatus['state'], 'stressed' | 'atRisk'>, string> = {
  thriving: TOKEN_TINT.good,
  ok: 'bg-muted text-foreground',
  slowed: 'bg-verdict-stress-tint text-foreground',
};

/**
 * The state's word, with a check for Thriving and a warning mark for At risk.
 *
 * @param props - WeekStateLabelProps.
 */
export const WeekStateLabel: React.FC<WeekStateLabelProps> = ({ status, className, iconClassName = 'h-3.5 w-3.5' }) => {
  const { reactTranslate: t } = useTranslation();
  const tone = status.state === 'stressed'
    ? (status.token ? TOKEN_TINT[status.token] : TONE.slowed)
    : status.state === 'atRisk'
      ? (status.token ? TOKEN_SOLID[status.token] : 'bg-foreground text-background')
      : TONE[status.state];
  return (
    <span className={cn('inline-flex items-center gap-1 whitespace-nowrap font-bold', tone, className)}>
      {status.state === 'thriving' && <Check className={cn('shrink-0', iconClassName)} aria-hidden="true" />}
      {status.state === 'atRisk' && <AlertTriangle className={cn('shrink-0', iconClassName)} aria-hidden="true" />}
      {t(`plants.card.state.${status.state}`)}
    </span>
  );
};
